/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {BusinessSettingsModal} from '../business-settings-modal';
import {BusinessSettingsModalState} from '../business-settings-modal.state';
import {TransportModeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/transport-mode.model';
import {InputListModel} from '../input-list.model';
import {EntityElementModel} from '../../../../shared/entity-tree/entity-element/models/entity-element.model';
import {ValidatorFn, Validators} from '@angular/forms';
import {InputTypeEnum} from '../../../../shared/input/input-type.enum';
import {InputTextComponent} from '../../../../shared/input/input-text/input-text.component';
import {take} from 'rxjs/operators';
import {combineLatest, Observable, of, timer} from 'rxjs';
import {InputDropdownComponent} from '../../../../shared/input/input-dropdown/input-dropdown.component';
import {TypeaheadHelperService} from '../../../../shared/services/typeahead-helper.service';
import {InputMessagesService} from '../../../../shared/input/service/input-messages.service';
import {BusinessSettingsApi} from '../../../../api/ms-settings/business/schema/v1/business-settings.api';
import {TransportDocumentTypeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/transport-document-type.model';
import {MandatoryMessageTypeEnum} from '../../../../shared/input/service/mandatory-message-type.enum';
import {WarningsEnum} from '../../../../shared/warnings/warnings.enum';

@Component({
    selector: 'app-transport-mode-modal',
    templateUrl: './transport-mode-modal.component.html',
    styleUrls: ['../business-settings-modal.css', './transport-mode-modal.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TransportModeModalComponent extends BusinessSettingsModal<BusinessSettingsModalState<TransportModeModel>> {

    @ViewChild('name', {static: true}) nameInput: InputTextComponent;
    @ViewChild('code', {static: true}) codeInput: InputTextComponent;
    @ViewChild('transportDocumentType', {static: true}) transportDocumentTypeComponent: InputDropdownComponent;

    readonly EDIT_TITLE = 'Edit transport mode';
    readonly ADD_TITLE = 'Add a transport mode';
    readonly EDIT_SUBMIT_TITLE = 'Edit';
    readonly ADD_SUBMIT_TITLE = 'Add';
    readonly ENTITY_NAME = 'transport mode';
    public nameMessages = new Map<WarningsEnum, string[]>();
    public codeMessages = new Map<WarningsEnum, string[]>();
    public transportDocumentTypeMessages = new Map<WarningsEnum, string[]>();

    validators: { [key: string]: ValidatorFn[] } = {
        name: [Validators.pattern(/^[\w-/\s\\.(),]{1,100}$/)],
        code: [Validators.pattern(/^\d{2}$/)]
    };

    private transportDocumentTypesList: TransportDocumentTypeModel[];

    constructor(public typeahead: TypeaheadHelperService,
                private inputMessagesService: InputMessagesService, private settingsApi: BusinessSettingsApi) {
        super(inputMessagesService);
    }

    getInitialData(): void {
        this.settingsApi.getAllTransportDocumentTypesOrderedByName()
.pipe(take(1))
                .subscribe((types: TransportDocumentTypeModel[]) => {
                    this.transportDocumentTypesList = types;
                });
    }

    setEntity(element: EntityElementModel<TransportModeModel>): void {
        this.storeState({
            originalElement: element,
            originalValue: element.originalValue,
            currentValue: {...element.currentValue}
        });

        combineLatest([this.settingsApi.getAllTransportDocumentTypesOrderedByName(), this.onReady])
                .pipe(take(1))
                .subscribe(([transportDocumentTypes, isReady]: [TransportDocumentTypeModel[], boolean]) => {
                    timer(0)
.subscribe(() => {
                        this.nameInput.setValue(this.state.currentValue.name);
                        this.codeInput.setValue(this.state.currentValue.code);
                        this.setActiveInputState(element.active, true);

                        this.transportDocumentTypesList = transportDocumentTypes;

                        const transportDocumentType = transportDocumentTypes
                                .find((type) => type.code === element.currentValue.documentTypeCode);

                        this.storeState({
                            currentValue: {
                                ...element.currentValue,
                                documentTypeCode: transportDocumentType == null ? null : transportDocumentType.code
                            }
                        });
                        this.transportDocumentTypeComponent.setValue(transportDocumentType);
                    });
                });
    }

    /**
     * Filter transport document types list (autocomplete)
     */
    searchTransportDocumentTypes = (term: string): Observable<any> => {
        let types: TransportDocumentTypeModel[] = this.transportDocumentTypesList;
        if (term != null && term.length > 0) {
            types = this.transportDocumentTypesList
                    .filter((value) => `${value.name}`.toLowerCase()
.indexOf(term.toLowerCase()) > -1);
        }
        return of(types);
    };

    nameInputChange(name: string): void {
        this.inputMessages.mandatoryWarnings(this.nameInput.inputControl, this.nameMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(this.nameInput.inputControl, this.nameMessages, this.nameInput.label);
        this.nameMessages = new Map(this.nameMessages);
        const errors = this.nameMessages.get(WarningsEnum.ERROR);

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                name: name
            },
            invalid: {
                ...this.state.invalid,
                name: (name == null || name.length === 0) || (errors != null && errors.length > 0)
            }
        });

        this.setModalValidity();
    }

    codeInputChange(code: string): void {
        this.inputMessages.mandatoryWarnings(this.codeInput.inputControl, this.codeMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(
                this.codeInput.inputControl,
                this.codeMessages,
                this.codeInput.label,
                '86'
        );

        this.codeMessages = new Map(this.codeMessages);

        const errors = this.codeMessages.get(WarningsEnum.ERROR);

        if (errors.length === 0) {
            this.manageAlreadyExistsCode(code, this.ENTITY_NAME, this.codeMessages, this.codeInput);
        }

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                code: code
            },
            invalid: {
                ...this.state.invalid,
                code: (code == null || code.length === 0) || (errors != null && errors.length > 0)
            }
        });

        this.setModalValidity();
    }

    transportDocumentTypeInputChange(transportDocumentType: TransportDocumentTypeModel): void {
        this.inputMessagesService.invalidInputValueError(this.transportDocumentTypeComponent.inputControl,
                this.transportDocumentTypeMessages,
                this.transportDocumentTypeComponent.label);
        this.transportDocumentTypeMessages = new Map(this.transportDocumentTypeMessages);

        let documentTypeCode: string = null;
        let invalid = false;
        if (transportDocumentType != null) {
            if (transportDocumentType.constructor !== ''.constructor) {
                documentTypeCode = transportDocumentType.code;
            } else if (transportDocumentType.constructor === ''.constructor) {
                invalid = true;
            }
        }

        // document type code is optional, is valid.
        this.storeState({
            currentValue: {...this.state.currentValue, documentTypeCode: documentTypeCode},
            invalid: {...this.state.invalid, transportDocumentType: invalid}
        });

        this.setModalValidity();
    }

    protected generateInputs(): InputListModel[] {
        return [{
            input: this.nameInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }, {
            input: this.codeInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }, {
            input: this.transportDocumentTypeComponent,
            type: InputTypeEnum.DROPDOWN,
            mandatory: false
        }];
    }
}
